import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import TextField from "@mui/material/TextField";

import useActions, { useAppSelector } from "../../../app/hooks";
import {
  selectProjectTaskInputs,
  selectProjectTaskOutputs,
} from "../../library/librarySlice";
import { Project } from "../../../types/Project";
import { ProjectTask } from "../../../types/ProjectTask";

export default function TaskBreakdownModal(props: {
  open: boolean;
  project: Project | null;
  task: ProjectTask | null;
  onClose: () => void;
}) {
  const actions = useActions();
  const inputs = useAppSelector(selectProjectTaskInputs);
  const outputs = useAppSelector(selectProjectTaskOutputs);
  const [newDescription, setNewDescription] = useState("");
  const [newContent, setNewContent] = useState("");
  const [expandedInputs, setExpandedInputs] = useState<Record<number, boolean>>({});
  const [editInputId, setEditInputId] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editToProcess, setEditToProcess] = useState<number>(0);
  const inputRowIndexRef = useRef(0);

  useEffect(() => {
    if (props.open && props.project && props.task) {
      actions.getProjectTask({
        projectId: props.project.id,
        taskId: props.task.id,
      });
    }
  }, [props.open, props.project?.id, props.task?.id]);

  if (!props.project || !props.task) return null;

  function handleCreateInput() {
    if (!props.project || !props.task) return;
    const desc = newDescription.trim();
    const content = newContent.trim();
    if (!desc || !content) return;

    actions.createProjectTaskInput({
      projectId: props.project.id,
      taskId: props.task.id,
      projectTaskInputCreateRequest: {
        input_description: desc,
        content,
      },
    });
    setNewDescription("");
    setNewContent("");
  }

  function openEditModal(inputId: number) {
    const input = inputs.find((i) => i.id === inputId);
    if (!input) return;
    setEditInputId(input.id);
    setEditDescription(input.input_description);
    setEditContent(input.content);
    setEditToProcess(input.to_process ?? 0);
  }

  function closeEditModal() {
    setEditInputId(null);
  }

  function handleSaveEdit() {
    if (!props.project || !props.task || editInputId == null) return;

    actions.updateProjectTaskInput({
      projectId: props.project.id,
      taskId: props.task.id,
      inputId: editInputId,
      projectTaskInputCreateRequest: {
        input_description: editDescription.trim(),
        content: editContent.trim(),
        to_process: editToProcess,
      },
    });
    closeEditModal();
  }

  // Build parent/child structure for inputs
  const childrenByParent: Record<number, typeof inputs> = {};
  const rootInputs: typeof inputs = [];
  inputRowIndexRef.current = 0;

  inputs.forEach((input) => {
    const parentId = input.derived_from_input_id ?? 0;
    if (parentId && parentId > 0) {
      if (!childrenByParent[parentId]) {
        childrenByParent[parentId] = [];
      }
      childrenByParent[parentId].push(input);
    } else {
      rootInputs.push(input);
    }
  });

  function handleToggleToProcess(inputId: number, currentToProcess: number) {
    if (!props.project || !props.task) return;
    const nextToProcess = currentToProcess === 0 ? 1 : 0;

    actions.toggleProjectTaskInput({
      projectId: props.project.id,
      taskId: props.task.id,
      inputId,
      to_process: nextToProcess,
    });
  }

  function handleDeleteInput(inputId: number) {
    if (!props.project || !props.task) return;
    if (!window.confirm("Delete this input?")) return;

    actions.deleteProjectTaskInput({
      projectId: props.project.id,
      taskId: props.task.id,
      inputId,
    });
  }

  const renderInput = (input: (typeof inputs)[number], depth = 0) => {
    const children = childrenByParent[input.id] ?? [];
    const hasChildren = children.length > 0;
    const isExpanded =
      expandedInputs[input.id] !== undefined
        ? expandedInputs[input.id]
        : true;

    const isDone = input.to_process === 0;
    const rowIndex = inputRowIndexRef.current++;
    const bgcolor = rowIndex % 2 === 0 ? "grey.100" : "grey.50";

    return (
      <React.Fragment key={input.id}>
        <ListItem
          alignItems="flex-start"
          sx={{
            mb: 1.5,
            pl: depth ? 3 : 0,
            bgcolor,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
            <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.25 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleToProcess(input.id, input.to_process ?? 0);
                }}
                aria-label={
                  isDone ? "Mark input as to process" : "Mark input as done"
                }
              >
                {isDone ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" color="disabled" />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteInput(input.id);
                }}
                aria-label="Delete input"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ListItemText
                primary={
                  <Typography sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    mt: 0.5,
                    overflowX: "scroll",
                    fontWeight: "bold",
                  }}>
                    {input.input_description}
                  </Typography>}
                secondary={
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      mt: 0.5,
                      overflowX: "scroll"
                    }}
                  >
                    {input.content}
                  </Typography>
                }
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={() =>
                    setExpandedInputs((prev) => ({
                      ...prev,
                      [input.id]: !isExpanded,
                    }))
                  }
                  aria-label={
                    isExpanded
                      ? "Collapse derived inputs"
                      : "Expand derived inputs"
                  }
                >
                  {isExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => openEditModal(input.id)}
                aria-label="Edit input"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </ListItem>
        {hasChildren &&
          isExpanded &&
          children.map((child) => renderInput(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Task breakdown: {props.task.name}
        </DialogTitle>
        <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            mt: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Inputs
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add input
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Description
                </Typography>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: 40,
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: "0.875rem",
                  }}
                />
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Content
                </Typography>
                <textarea
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: 80,
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: "0.875rem",
                  }}
                />
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleCreateInput}
                disabled={!newDescription.trim() || !newContent.trim()}
              >
                Add input
              </Button>
            </Box>
            {inputs.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No inputs recorded for this task.
              </Typography>
            )}
            {inputs.length > 0 && (
              <List dense>
                {rootInputs.map((input) => renderInput(input))}
              </List>
            )}
            
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              borderLeft: { md: "1px solid rgba(0,0,0,0.12)" },
              pl: { md: 3 },
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Outputs
            </Typography>
            {outputs.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No outputs recorded for this task.
              </Typography>
            )}
            {outputs.length > 0 && (
              <List dense>
                {outputs.map((output) => (
                  <ListItem
                    key={output.id}
                    alignItems="flex-start"
                    sx={{ mb: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        output.code_proposal_id != null
                          ? `Code proposal #${output.code_proposal_id}`
                          : "Output"
                      }
                      secondary={
                        <Typography
                          component="div"
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            mt: 0.5,
                          }}
                        >
                          {output.proposed_output_text ?? ""}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() =>
              props.project &&
              props.task &&
              actions.getProjectTask({
                projectId: props.project.id,
                taskId: props.task.id,
              })
            }
          >
            Refresh
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={props.onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editInputId != null}
        onClose={closeEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit input</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Description"
              fullWidth
              margin="dense"
              variant="standard"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Content"
              fullWidth
              margin="dense"
              variant="standard"
              multiline
              minRows={3}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="to_process"
              type="number"
              fullWidth
              margin="dense"
              variant="standard"
              value={editToProcess}
              onChange={(e) =>
                setEditToProcess(Number(e.target.value) || 0)
              }
              helperText="0 means completed; non-zero means pending/processing."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button onClick={closeEditModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!editDescription.trim() || !editContent.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

